/**
 * Admin System Health Dashboard — surfaces push pipeline status, failure
 * breakdown with auto-resolution log, devices, COA seed integrity, eCourts
 * queue depth, MongoDB indexes. One-screen ops view.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/time";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";
import { Activity, AlertTriangle, RefreshCw, CheckCircle2, ChevronDown, ChevronRight, Send, Smartphone, ShieldCheck, Database, X, Wifi, WifiOff, Loader2 } from "lucide-react";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const STATUS_COLOR = {
    sent: "border-emerald-500/40 text-emerald-400",
    delivered: "border-emerald-500/40 text-emerald-400",
    queued: "border-border text-muted-foreground",
    retrying: "border-amber-500/40 text-amber-400",
    dismissed: "border-blue-500/40 text-blue-400",
    failed: "border-rose-500/40 text-rose-400",
    failed_resolved: "border-emerald-500/40 text-emerald-400",
    failed_unresolved: "border-rose-500/40 text-rose-400",
};

function Stat({ label, value, sub, accent }) {
    return (
        <div className="border border-border bg-card p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
            <div className={`text-2xl mt-1 ${accent || "text-foreground"}`}>{value ?? "—"}</div>
            {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
        </div>
    );
}

function FailureDetail({ row, onClose, onRetry }) {
    return (
        <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-card border border-border w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}
                 data-testid="sh-failure-detail">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-foreground text-lg">{row.title}</h3>
                        <div className="text-muted-foreground text-[11px] mt-1">{row.body}</div>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4"/></button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs mb-4">
                    <div><span className="text-muted-foreground">Tenant: </span><span className="text-foreground font-mono">{row.tenant_id}</span></div>
                    <div><span className="text-muted-foreground">User: </span><span className="text-foreground font-mono">{row.user_id}</span></div>
                    <div><span className="text-muted-foreground">Topic: </span><span className="text-foreground">{row.topic}</span></div>
                    <div><span className="text-muted-foreground">Matched by: </span><span className="text-foreground">{row.matched_by}</span></div>
                    <div><span className="text-muted-foreground">Failure: </span><span className="text-rose-400">{row.failure_reason || "—"}</span></div>
                    <div><span className="text-muted-foreground">Attempts: </span><span className="text-foreground">{row.attempts ?? 0} / 5</span></div>
                    <div><span className="text-muted-foreground">Status: </span>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${STATUS_COLOR[row.status] || ""}`}>{row.status}</span>
                    </div>
                    <div><span className="text-muted-foreground">Next retry: </span><span className="text-foreground">{row.next_retry_at ? fmtDateTime(row.next_retry_at) : "—"}</span></div>
                </div>

                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Auto-resolution timeline</div>
                <div className="border border-border bg-background p-3 space-y-2 mb-4">
                    {(row.resolutions || []).length === 0 && (
                        <div className="text-muted-foreground text-xs">No resolution events yet.</div>
                    )}
                    {(row.resolutions || []).map((r, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                            {r.success === true ? <CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-400 shrink-0"/> :
                             r.success === false ? <AlertTriangle className="w-3 h-3 mt-0.5 text-rose-400 shrink-0"/> :
                             <Activity className="w-3 h-3 mt-0.5 text-muted-foreground shrink-0"/>}
                            <div className="flex-1 min-w-0">
                                <div className="text-foreground"><span className="font-mono text-primary">{r.action}</span> <span className="text-muted-foreground text-[10px]">{fmtDateTime(r.at)}</span></div>
                                <div className="text-muted-foreground break-all">{r.detail}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {row.last_error && (
                    <div className="border border-rose-500/30 bg-rose-500/5 p-3 mb-4">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-rose-400 mb-1">Last error</div>
                        <div className="text-xs text-foreground font-mono break-all">{row.last_error}</div>
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">Close</button>
                    {row.status !== "failed_resolved" && row.status !== "delivered" && (
                        <button onClick={() => onRetry(row.id)} data-testid="sh-failure-retry"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10">
                            <RefreshCw className="w-3 h-3"/>Retry now
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function AdminSystemHealth() {
    const [snap, setSnap] = useState(null);
    const [failures, setFailures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async () => {
        try {
            const [a, b] = await Promise.all([
                api.get("/admin/system-health"),
                api.get("/admin/system-health/push/failures?limit=100"),
            ]);
            setSnap(a.data); setFailures(b.data || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); setRefreshing(false); }
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const id = setInterval(() => { setRefreshing(true); load(); }, 30000);
        return () => clearInterval(id);
    }, [load]);

    const failuresCtrl = useTableControls(failures || [], {
        accessors: { tenant: (r) => r.tenant_id || "" },
    });

    const retry = async (logId) => {
        try {
            await api.post(`/admin/system-health/push/${logId}/retry`);
            toast.success("Retry queued. Will fire within 60s.");
            setDetail(null);
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    if (loading) {
        return <div className="text-muted-foreground text-sm">Loading…</div>;
    }
    if (!snap) {
        return <div className="text-rose-400 text-sm">Failed to load system health.</div>;
    }

    const p = snap.push;
    const reasons = Object.entries(p.failure_reasons_7d || {});

    return (
        <div className="space-y-6" data-testid="admin-system-health-page">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <PageHeader title="System health"
                    subtitle="Live ops view · push pipeline, devices, COA seed, eCourts queue · auto-refresh every 30 s."/>
                <div className="flex items-center gap-2">
                    <ScrubDemoDataButton/>
                    <button onClick={() => { setRefreshing(true); load(); }} disabled={refreshing}
                        data-testid="sh-refresh"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border text-muted-foreground hover:text-primary hover:border-primary text-[10px] uppercase tracking-[0.16em]">
                        {refreshing ? <Loader2 className="w-3 h-3 animate-spin"/> : <RefreshCw className="w-3 h-3"/>}Refresh
                    </button>
                </div>
            </div>

            {/* Top-line stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sh-stats">
                <Stat label="Scheduler"
                    value={p.scheduler_alive ? <span className="inline-flex items-center gap-2"><Wifi className="w-5 h-5 text-emerald-400"/>Alive</span> : <span className="inline-flex items-center gap-2"><WifiOff className="w-5 h-5 text-rose-400"/>Stalled</span>}
                    sub={p.last_push_at ? `Last push ${new Date(p.last_push_at).toLocaleString()}` : "No push yet"}/>
                <Stat label="Queued for retry" value={p.queued_for_retry} accent={p.queued_for_retry > 0 ? "text-amber-400" : "text-foreground"}/>
                <Stat label="Auto-resolved" value={p.auto_resolved} accent="text-emerald-400" sub="DeviceNotRegistered + retried OK"/>
                <Stat label="Unresolved" value={p.unresolved} accent={p.unresolved > 0 ? "text-rose-400" : "text-foreground"} sub={p.unresolved > 0 ? "Manual attention needed" : "All clear"}/>
            </div>

            {/* Last 24h breakdown */}
            <div>
                <h3 className="text-foreground text-sm mb-3 flex items-center gap-2"><Send className="w-4 h-4 text-primary"/>Last 24 hours · {Object.values(p.status_24h || {}).reduce((a, b) => a + b, 0)} pushes</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                    {Object.entries(p.status_24h || {}).map(([s, n]) => (
                        <div key={s} className="border border-border bg-card p-3" data-testid={`sh-status-${s}`}>
                            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${STATUS_COLOR[s] || ""}`}>{s}</span>
                            <div className="text-xl text-foreground mt-2">{n}</div>
                        </div>
                    ))}
                    {Object.keys(p.status_24h || {}).length === 0 && (
                        <div className="col-span-full text-muted-foreground text-xs">No push activity in the last 24 hours.</div>
                    )}
                </div>
            </div>

            {/* Top failure reasons */}
            {reasons.length > 0 && (
                <div>
                    <h3 className="text-foreground text-sm mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400"/>Top failure reasons (7 days)</h3>
                    <div className="border border-border bg-card divide-y divide-border">
                        {reasons.map(([reason, count]) => (
                            <div key={reason} className="flex items-center justify-between px-3 py-2 text-xs">
                                <span className="text-foreground font-mono">{reason}</span>
                                <span className="text-muted-foreground">{count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Failures list */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-foreground text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-rose-400"/>Failed / retrying pushes</h3>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{failures.length} entries</span>
                </div>
                <div className="border border-border bg-card overflow-x-auto">
                    <FilterChip ctrl={failuresCtrl} className="m-2"/>
                    <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                            <tr className="border-b border-border">
                                <SortableTh field="updated_at" ctrl={failuresCtrl} className="text-left px-3 py-2">When</SortableTh>
                                <SortableTh field="tenant" ctrl={failuresCtrl} className="text-left px-3 py-2 hidden md:table-cell">Tenant</SortableTh>
                                <SortableTh field="topic" ctrl={failuresCtrl} className="text-left px-3 py-2">Topic</SortableTh>
                                <SortableTh field="title" ctrl={failuresCtrl} className="text-left px-3 py-2">Title</SortableTh>
                                <SortableTh field="failure_reason" ctrl={failuresCtrl} className="text-left px-3 py-2">Reason</SortableTh>
                                <SortableTh field="attempts" ctrl={failuresCtrl} className="text-left px-3 py-2 hidden md:table-cell">Attempts</SortableTh>
                                <SortableTh field="status" ctrl={failuresCtrl} className="text-left px-3 py-2">Status</SortableTh>
                                <th className="text-right px-3 py-2">&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            {failuresCtrl.rows.length === 0 && (
                                <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground"><CheckCircle2 className="w-5 h-5 inline mr-2 text-emerald-400"/>No failures right now — everything's flowing.</td></tr>
                            )}
                            {failuresCtrl.rows.map(r => (
                                <tr key={r.id} className="border-b border-card hover:bg-muted/40" data-testid={`sh-failure-${r.id}`}>
                                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmtDateTime(r.updated_at)}</td>
                                    <td className="px-3 py-2 text-muted-foreground font-mono hidden md:table-cell">{(r.tenant_id || "").slice(-8)}</td>
                                    <td className="px-3 py-2 text-foreground">{r.topic}</td>
                                    <td className="px-3 py-2 text-foreground truncate max-w-[200px]">{r.title}</td>
                                    <td className="px-3 py-2 text-rose-400 font-mono">{r.failure_reason || "—"}</td>
                                    <td className="px-3 py-2 text-foreground hidden md:table-cell">{r.attempts ?? 0}/5</td>
                                    <td className="px-3 py-2">
                                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${STATUS_COLOR[r.status] || ""}`}>{r.status}</span>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <button onClick={() => setDetail(r)} data-testid={`sh-open-${r.id}`}
                                            className="text-[10px] uppercase tracking-wider px-2 py-1 border border-border text-muted-foreground hover:text-primary hover:border-primary">
                                            Detail
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Devices + COA + indexes */}
            <div className="grid lg:grid-cols-2 gap-4">
                <div>
                    <h3 className="text-foreground text-sm mb-2 flex items-center gap-2"><Smartphone className="w-4 h-4 text-primary"/>Mobile devices · {snap.devices.total} total</h3>
                    <div className="border border-border bg-card max-h-60 overflow-auto">
                        {(snap.devices.by_tenant || []).length === 0 && (
                            <div className="px-3 py-4 text-muted-foreground text-xs">No devices registered.</div>
                        )}
                        {(snap.devices.by_tenant || []).map(d => (
                            <div key={d.tenant_id} className="flex justify-between px-3 py-2 text-xs border-b border-card">
                                <span className="text-foreground font-mono">{(d.tenant_id || "—").slice(-12)}</span>
                                <span className="text-muted-foreground">{d.devices}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div>
                    <h3 className="text-foreground text-sm mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400"/>COA seed health</h3>
                    <div className="border border-border bg-card p-3 text-xs space-y-1">
                        <div className="flex justify-between"><span className="text-muted-foreground">Platform accounts</span><span className="text-foreground">{snap.coa.platform_accounts}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Tenants</span><span className="text-foreground">{snap.coa.tenants}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Drifted tenants</span>
                            <span className={snap.coa.drifted_tenants.length > 0 ? "text-amber-400" : "text-emerald-400"}>
                                {snap.coa.drifted_tenants.length}
                            </span>
                        </div>
                        <div className="flex justify-between"><span className="text-muted-foreground">eCourts pending syncs</span><span className="text-foreground">{snap.ecourts.pending_syncs}</span></div>
                    </div>
                </div>
            </div>

            <details className="border border-border bg-card p-3">
                <summary className="cursor-pointer text-foreground text-sm flex items-center gap-2"><Database className="w-4 h-4"/>MongoDB indexes</summary>
                <div className="grid md:grid-cols-2 gap-2 mt-3 text-xs">
                    {(snap.indexes || []).map(i => (
                        <div key={i.collection} className="border border-border bg-background p-2">
                            <div className="text-foreground font-mono mb-1">{i.collection}</div>
                            <div className="text-muted-foreground">{(i.indexes || []).join(", ")}</div>
                        </div>
                    ))}
                </div>
            </details>

            {detail && <FailureDetail row={detail} onClose={() => setDetail(null)} onRetry={retry}/>}
        </div>
    );
}

function ScrubDemoDataButton() {
    const [busy, setBusy] = useState(false);
    const scrub = async () => {
        if (!window.confirm("Hard-delete the 'demofirm' demo tenant and every row tied to it (clients, cases, hearings, invoices, journal entries, etc.) across 31 collections?\n\nThis is safe on production but cannot be undone.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/platform/scrub-demo-data");
            if (data.tenant_removed) {
                toast.success(`Scrubbed ${data.rows_deleted} rows across ${Object.keys(data.breakdown).length} collections`);
            } else if (data.owner_user_removed) {
                toast.success("Removed stale demo owner user");
            } else {
                toast.success("All clear — no demo data found");
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <button onClick={scrub} disabled={busy} data-testid="sh-scrub-demo"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-amber-500/40 text-amber-500 hover:bg-amber-500/10 text-[10px] uppercase tracking-[0.16em]"
            title="Hard-delete the demofirm demo tenant + all its rows">
            {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : <AlertTriangle className="w-3 h-3"/>}
            Scrub demo data
        </button>
    );
}
